import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useEditMode, useIsOnlyProjectLeader, useNotification } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import {
  ProjectAcronym,
  useCreateProjectMutation,
  useGetAcronymsQuery,
  useLazyGetProjectSettingsByIdQuery,
} from '../../store/redux/reducers/projectsSlice'
import { useGetLightProjectLeadersQuery } from '../../store/redux/reducers/usersSlice'
import { RequestProps } from '../../store/types'
import { StyledPageContent } from '../../styles/PageStyles'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import ProjectPicture from '../../components/avatar/ProjectPicture'
import { LoadingButton } from '@mui/lab'
import TextInput from '../../components/Form/Inputs/TextInput'
import SelectInput, { Option } from '../../components/Form/Inputs/SelectInput'
import ProjectStatus from '../../store/types/enums/ProjectStatus'
import AutocompleteInput from '../../components/Form/Inputs/AutocompleteInput'
import CheckboxInput from '../../components/Form/Inputs/CheckboxInput'
import { Box, Grid, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Add, InfoOutlined } from '@mui/icons-material'
import SelectProjectLeaders from '../../components/Form/SelectProjectLeaders'
import { projectDefaultFormValues, ProjectFormValues } from '../../store/types/form/ProjectFormValues'
import User from '../../store/types/models/User'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'
import TeamTable from '../../components/Table/TeamTable'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { useSelector } from 'react-redux'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import { isUrl } from '../../store/utils/isUrlUtils'
import { createErrorMessage } from '../../store/utils'
import { filterOptions } from '../../store/utils/filterProjectsAcronymsOptions'

const AddProjectView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)
  const isOnlyProjectLeader = useIsOnlyProjectLeader()
  const { displayNotification } = useNotification()
  const theme = useTheme()
  const isLargeSize = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)
  const { data: leadersData } = useGetLightProjectLeadersQuery({ page: 0, size: 20, employeeStatus: 'ACTIVE' })
  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const { setIsEditMode } = useEditMode()
  const projectsProps: RequestProps = useMemo(() => {
    return { page: page, size: 20, acronym: searchedAcronym || undefined, sortByWorkHours: false, parent: 'null' }
  }, [page, searchedAcronym])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const leaders = useMemo(() => {
    return leadersData ? leadersData.content : []
  }, [leadersData])

  const { data: projects } = useGetAcronymsQuery(projectsProps)
  const [avatar, setAvatar] = useState<string | undefined>()
  const [extension, setExtension] = useState<string | undefined>()

  const [displayedProjects, setDisplayedProjects] = useState<ProjectAcronym[]>([])
  const projectStatusOptions: Option[] = [
    { value: ProjectStatus.ACTIVE, label: t('page.projects.filterPopup.active') },
    { value: ProjectStatus.INACTIVE, label: t('page.projects.filterPopup.inactive') },
    { value: ProjectStatus.TRIAL, label: t('page.projects.filterPopup.trial') },
    { value: ProjectStatus.FINISHED, label: t('page.projects.filterPopup.finished') },
  ]

  const onSelectBottomScroll = () => {
    if (!projects?.last) {
      handleFetchMoreData()
    }
  }

  const handleFetchMoreData = () => {
    setPage(prev => prev + 1)
  }

  useEffect(() => {
    setIsEditMode(true)
  }, [setIsEditMode])

  useEffect(() => {
    if (projects) {
      setDisplayedProjects(prevProjects => {
        const existingProjectIds = new Set(prevProjects.map(project => project.id))
        const newProjects = projects.content.filter(project => !existingProjectIds.has(project.id))
        return [...prevProjects, ...newProjects]
      })
    }
  }, [projects])

  const { handleSubmit, control, formState, reset, setValue, watch, getValues } = useForm<ProjectFormValues>({
    mode: 'onChange',
    defaultValues: {
      ...projectDefaultFormValues,
      leaders: currentUser && isOnlyProjectLeader ? [currentUser] : [],
    },
  })
  const { isSubmitting, isSubmitSuccessful, isDirty } = formState
  const [createProject] = useCreateProjectMutation()

  useEffect(() => {
    dispatch(setPageName(t('page.name.newProject')))
    dispatch(setPageIndex(PageIndex.PROJECTS))
    dispatch(pageHeaderActions.setInitialState())
  })

  useEffect(() => {
    formState.isValid ? setSaveButtonDisabled(false) : setSaveButtonDisabled(true)
  }, [formState.isValid])

  const handleCreate = async (data: ProjectFormValues) => {
    try {
      const response = await createProject({
        ...data,
        leaders: data.leaders?.map(leader => ({
          projectId: '',
          leaderId: leader.id,
          main: leader.main ?? false,
        })),
        parentId: data.parent?.value,
      }).unwrap()
      displayNotification({ message: t('page.projects.notificationPopup.projectCreated'), type: 'success' })
      setIsEditMode(false)
      reset(data)
      let id = response.id
      setTimeout(() => navigate(generatePath(navigation.projectDetails, { id })), 0)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('page.users.message.error')} ${errorDescription}`,
        type: 'error',
      })
      reset(data)
    }
  }

  const handleFormSubmit = async (data: ProjectFormValues) => {
    handleCreate(data)
  }

  const updateViewValues = (avatar: string | undefined, extension: string | undefined) => {
    if (avatar && extension) {
      setAvatar(avatar)
      setExtension(extension)
    } else if (!avatar && !extension) {
      setAvatar(undefined)
      setExtension(undefined)
    }
  }

  const { replace } = useFieldArray({
    name: 'team',
    control,
  })

  const replaceArray = (team: User[]) => {
    return replace(team)
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty || watch('avatar') !== formState.defaultValues?.avatar
  }

  const watchProjectStatus = watch('projectStatus')

  useEffect(() => {
    if (
      watchProjectStatus &&
      watchProjectStatus !== ProjectStatus.ACTIVE &&
      watchProjectStatus !== ProjectStatus.TRIAL
    ) {
      setValue('loggable', false)
    }
  }, [watchProjectStatus, setValue])

  const [getParentSettings] = useLazyGetProjectSettingsByIdQuery()

  const watchParent = watch('parent')

  const handleGetSettings = useCallback(
    async (id: string) => {
      try {
        const settingsResult = await getParentSettings(id)
        if (settingsResult.isSuccess) {
          return settingsResult.data
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error)
      }
      return null
    },
    [getParentSettings]
  )

  useEffect(() => {
    if (watchParent?.value) {
      const fetchSettings = async () => {
        const settings = await handleGetSettings(watchParent.value)
        if (settings) {
          setValue('leaders', settings.leaders)
          setValue('privacy', settings.privacy)
          setValue('projectStatus', settings.projectStatus)
          setValue('loggable', settings.loggable)
          setValue('operational', settings.operational)
        }
      }

      fetchSettings()
    }
  }, [watchParent, handleGetSettings, setValue])

  return (
    <StyledPageContent sx={{ color: 'text.light' }}>
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <Stack direction={isDownSm ? 'column' : 'row'} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isDownSm ? 'column' : 'row',
              alignItems: 'center',
              gap: 2,
              padding: 2,
            }}
          >
            <ProjectPicture
              key={avatar}
              extension={extension}
              avatar={avatar}
              alt="Project Avatar"
              editable={true}
              updateAvatar={avatar => {
                setValue('avatar', avatar)
              }}
              updateExtension={extension => setValue('extension', extension)}
              onChange={updateViewValues}
            />
            <Typography component={'h2'} variant="h5">
              {t('page.projects.editProject.newProject')}
            </Typography>
          </Box>
          <LoadingButton
            type="submit"
            sx={{ mr: isDownSm ? 0 : 2 }}
            variant="contained"
            color="primary"
            disabled={saveButtonDisabled || isSubmitting || isSubmitSuccessful}
            loading={isSubmitting || isSubmitSuccessful}
            startIcon={<Add />}
            size={isDownSm ? 'small' : 'medium'}
          >
            {t('page.projects.editProject.saveProject')}
          </LoadingButton>
        </Stack>
        <Grid container display={'flex'} justifyContent={'end'} sx={{ paddingBottom: 3 }}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ py: 2, px: isDownSm ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 2 }}>
              <Typography component={'h3'} variant="h5">
                {t('page.projects.editProject.projectInfo')}
              </Typography>
              <Typography variant="caption">{t('page.usersProfile.form.headers.requiredFields')}</Typography>
            </Box>
            <Stack direction={isLargeSize ? 'column' : 'row'} sx={{ gap: 2 }}>
              <Stack direction="column" gap={2} width={'100%'}>
                <TextInput
                  name={'name'}
                  control={control}
                  label={t('page.projects.editProject.fullName')}
                  placeholder={t('page.projects.editProject.fullName')}
                  maxLength={50}
                  required
                  rules={{
                    required: t('components.costSidebar.form.errors.amountRequired'),
                    maxLength: { value: 50, message: t('page.projects.editProject.nameLengthError') },
                  }}
                />
                <TextInput
                  name={'acronym'}
                  control={control}
                  label={t('components.table.header.acronym')}
                  placeholder={t('components.table.header.acronym')}
                  maxLength={20}
                  required
                  rules={{
                    required: t('page.projects.form.required.acronym'),
                    maxLength: { value: 20, message: t('page.projects.editProject.acronymLengthError') },
                  }}
                />

                <Stack direction={'row'} sx={{ alignItems: 'center', gap: 1 }}>
                  <AutocompleteInput
                    options={displayedProjects.map(project => ({
                      label: `${project.acronym}`,
                      value: project.id,
                    }))}
                    name={'parent'}
                    control={control}
                    label={t('page.projects.editProject.parentProject')}
                    onSelectBottomScroll={onSelectBottomScroll}
                    handleInputChange={handleInputChange}
                    filterOptions={(options, state) => filterOptions(options, state, displayedProjects)}
                  />
                  <Tooltip title={t('page.projects.editProject.parentProjectInfo')}>
                    <InfoOutlined />
                  </Tooltip>
                </Stack>

                <TextInput
                  name={'description'}
                  control={control}
                  label={t('components.table.header.description')}
                  placeholder={t('page.projects.editProject.enterDescription')}
                  maxLength={500}
                  rules={{
                    maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
                  }}
                  multiline
                  rows={6}
                  showCharCounter
                />

                <TextInput
                  name={'driveUrl'}
                  control={control}
                  label={t('page.projects.editProject.driveUrl')}
                  rules={{
                    maxLength: { value: 100, message: t('page.projects.form.driveUrlMax', { value: 100 }) },
                    validate: { isUrl },
                  }}
                  maxLength={100}
                />

                <SelectInput
                  options={projectStatusOptions}
                  name={'projectStatus'}
                  control={control}
                  required={true}
                  label={'Status'}
                  rules={{ required: t('page.projects.form.required.projectStatus') }}
                />

                <CheckboxInput
                  name={'privacy'}
                  control={control}
                  label={t('page.projects.form.privateProjectDescription')}
                />

                {isManagement && (
                  <CheckboxInput name={'operational'} control={control} label={t('page.projects.form.operational')} />
                )}

                <CheckboxInput
                  name={'loggable'}
                  control={control}
                  label={t('page.projects.form.loggable')}
                  disabled={watchProjectStatus !== ProjectStatus.ACTIVE && watchProjectStatus !== ProjectStatus.TRIAL}
                />
              </Stack>

              <Stack direction="column" gap={2} width={'100%'}>
                <SelectProjectLeaders leaders={leaders} control={control} />
              </Stack>
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{ pt: 2, pb: 0, px: isDownSm ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <StyledContainerRowBetween>
              <Typography component={'h3'} variant="h5" color="text.mediumEmphasis" id={'team-header'}>
                {t('page.projects.detailsView.team')}
              </Typography>
            </StyledContainerRowBetween>
            <TeamTable
              editable={true}
              replace={replaceArray}
              watchedUsers={watch('team')}
              users={[]}
              leaders={getValues('leaders')}
            />
          </Grid>
        </Grid>
      </form>
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </StyledPageContent>
  )
}

export default AddProjectView
